<template>
  <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
    <div
        class="bg-white rounded-xl max-w-lg w-full shadow-lg relative select-none border border-gray-200 overflow-hidden print:max-w-full print:w-full print:shadow-none print:rounded-none"
    >
      <!-- 티켓 헤더 -->
      <div class="bg-blue-600 text-white px-6 py-4 flex justify-between items-center">
        <h3 class="text-lg font-bold">예약 확인증</h3>
        <button @click="$emit('close')" class="text-white/80 hover:text-white print:hidden">
          <XMarkIcon class="w-6 h-6" />
        </button>
      </div>

      <!-- 본문 -->
      <div class="p-6 space-y-4">
        <!-- 행사 정보 -->
        <div>
          <h4 class="text-xl font-bold text-gray-900 mb-1">
            {{ reservation?.post.title }}
          </h4>
          <p class="text-sm text-gray-600">{{ reservation?.post.place }}</p>
          <p class="text-sm text-gray-600">
            {{ reservation ? formatDate(reservation.post.startDate) : '' }} ~
            {{ reservation ? formatDate(reservation.post.endDate) : '' }}
          </p>
        </div>

        <!-- 점선 구분선 -->
        <div class="border-t border-dashed border-gray-300 my-4"></div>

        <!-- 예약자/좌석/상태 -->
        <div class="space-y-3">
          <div class="flex items-center text-sm text-gray-700">
            <UserIcon class="w-4 h-4 mr-2 text-gray-500" />
            {{ reservation?.user.name }} ({{ reservation?.user.email }})
          </div>
          <div class="flex items-center text-lg font-semibold text-blue-600">
            <TicketIcon class="w-5 h-5 mr-2" />
            {{ reservation?.seat.seatNumber }}번 좌석
          </div>
          <div class="flex items-center text-sm text-gray-700">
            <CheckCircleIcon
                v-if="effectiveStatus === 'ACTIVE'"
                class="w-4 h-4 mr-2 text-green-600"
            />
            <ClockIcon
                v-else-if="effectiveStatus === 'EXPIRED'"
                class="w-4 h-4 mr-2 text-gray-500"
            />
            <XCircleIcon v-else class="w-4 h-4 mr-2 text-red-600" />
            {{ statusText }}
          </div>
          <div class="flex items-center text-sm text-gray-700">
            <CalendarDaysIcon class="w-4 h-4 mr-2 text-gray-500" />
            예약일: {{ reservation ? formatDate(reservation?.createdAt) : '' }}
          </div>
        </div>

        <!-- QR 코드 -->
        <div class="flex justify-center py-4">
          <QrcodeVue :value="qrData" :size="128" level="H" />
        </div>
      </div>

      <!-- 점선 + 푸터 -->
      <div class="border-t border-dashed border-gray-300"></div>
      <div class="p-6 bg-gray-50 flex justify-end gap-3 print:hidden">
        <button @click="$emit('close')" class="btn btn-outline btn-sm">닫기</button>
        <button @click="printTicket" class="btn btn-primary btn-sm">인쇄하기</button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  XMarkIcon,
  UserIcon,
  TicketIcon,
  CalendarDaysIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/vue/24/outline'
import QrcodeVue from 'qrcode.vue'
import type { Reservation } from '@/types'
import { computed } from 'vue'

// Props
interface Props {
  reservation: Reservation | null
}
const props = defineProps<Props>()

const emit = defineEmits<{ close: []; print: [] }>()

const effectiveStatus = computed(() => {
  if (!props.reservation) return 'CANCELLED'
  if (props.reservation.effectiveStatus) return props.reservation.effectiveStatus
  if (props.reservation.status === 'CANCELLED') return 'CANCELLED'
  if (new Date(props.reservation.post.endDate) < new Date()) return 'EXPIRED'
  return 'ACTIVE'
})

// 상태 텍스트
const statusText = computed(() => {
  if (effectiveStatus.value === 'ACTIVE') return '예약 완료'
  if (effectiveStatus.value === 'EXPIRED') return '만료됨'
  return '예약 취소됨'
})

// 날짜 포맷
const formatDate = (dateString: string) => {
  const date = new Date(dateString)
  return date.toLocaleString('ko-KR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

// QR 데이터 (백엔드 인증용 URL)
const qrData = computed(() => {
  if (!props.reservation) return ''
  if (!props.reservation.verificationToken) return ''
  const token = encodeURIComponent(props.reservation.verificationToken)
  return `${import.meta.env.VITE_API_BASE_URL}/reservations/verify?token=${token}`
})

// 인쇄
const printTicket = () => {
  emit('print')
  window.print()
}
</script>

<style scoped>
/* 복사 방지 */
.select-none {
  user-select: none;
}

/* 프린트 스타일 */
@media print {
  body * {
    visibility: hidden;
  }
  .print\:max-w-full,
  .print\:max-w-full * {
    visibility: visible;
  }
  .print\:max-w-full {
    position: absolute;
    left: 0;
    top: 0;
  }
}
</style>
